import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * One-time blocking modal shown to logged-in users who have NOT yet
 * acknowledged the educational-use disclaimer. Accounts that pre-date the
 * feature get prompted here so we can stamp `disclaimer_acknowledged_at` and
 * unlock AI features (posture analysis, SOAP draft, protocol suggest).
 */
export default function DisclaimerGateModal() {
  const { user, refreshUser } = useAuth();
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  if (user.disclaimer_acknowledged_at) return null;

  const accept = async () => {
    if (!ack) return;
    setSaving(true);
    try {
      await api.post("/auth/acknowledge-disclaimer");
      await refreshUser();
      toast.success("Thanks — AI features are now unlocked.");
    } catch {
      toast.error("Could not record acknowledgement. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="disclaimer-gate-modal"
    >
      <div className="bg-white rounded-3xl w-full max-w-xl p-6 sm:p-8 shadow-2xl">
        <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-terracotta-500/10 text-terracotta-600 text-xs">
          <ShieldAlert className="w-3.5 h-3.5" />
          <span className="uppercase tracking-[0.14em]">One-time acknowledgement</span>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-light text-ink-900 mb-3 leading-tight">
          Before you use AI features, please accept the educational-use terms.
        </h2>
        <p className="text-sm text-ink-700 leading-relaxed mb-5">
          Posturra is intended for <strong className="text-ink-900">educational, documentation, and professional support purposes only</strong>.
          AI-generated posture analysis, SOAP drafts, and protocol suggestions are informational tools — not medical diagnoses,
          not treatment prescriptions, and not a substitute for licensed medical evaluation.
        </p>

        <label className="flex items-start gap-3 text-sm text-ink-700 cursor-pointer select-none bg-paper-50 border border-paper-200 rounded-2xl p-4 mb-5">
          <input
            type="checkbox"
            data-testid="gate-ack-checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-paper-200 text-sage-500 focus:ring-sage-500/30"
          />
          <span>
            I acknowledge that all treatment decisions remain my responsibility as a licensed practitioner.
            AI outputs may be incomplete or inaccurate and will not be treated as definitive conclusions.{" "}
            <Link
              to="/disclaimer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-terracotta-500 font-medium underline-offset-4 hover:underline"
              data-testid="gate-full-disclaimer-link"
            >
              Read the full disclaimer
            </Link>
            .
          </span>
        </label>

        <div className="flex items-center justify-end">
          <button
            data-testid="gate-accept-btn"
            onClick={accept}
            disabled={!ack || saving}
            className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <Check className="w-4 h-4" />
            {saving ? "Saving…" : "I accept — unlock AI features"}
          </button>
        </div>
      </div>
    </div>
  );
}
